import React, { useState, useEffect } from 'react';
import { apiFetch } from './utils/api';

function SettingsPPMPricing() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await apiFetch('/api/ppm/pricing-config');
      const data = await response.json();
      setConfig(data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching pricing config:', err);
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);

    try {
      const response = await apiFetch('/api/ppm/pricing-config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config)
      });

      if (!response.ok) throw new Error('Failed to save pricing config');

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Error saving pricing config:', err);
      alert('Failed to save pricing config: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setConfig({ ...config, [field]: parseFloat(value) || 0 });
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-gray-600">Loading pricing configuration...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-4xl">
        {/* Header */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            PPM Pricing Configuration
          </h2>
          <p className="text-gray-600">
            Configure rates for PPM quotes and contracts. Changes apply to new quotes immediately.
          </p>
        </div>

        <div className="space-y-6">
          {/* Service Rates */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Service Rates</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Base Rate (1st Asset inc. Travel)
                  </label>
                  <div className="flex items-center">
                    <span className="text-gray-500 mr-2">£</span>
                    <input
                      type="number"
                      step="0.01"
                      value={config.base_rate}
                      onChange={(e) => updateField('base_rate', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Price for the first automatic asset (includes travel cost)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Additional Automatic Door
                  </label>
                  <div className="flex items-center">
                    <span className="text-gray-500 mr-2">£</span>
                    <input
                      type="number"
                      step="0.01"
                      value={config.automatic_door_rate}
                      onChange={(e) => updateField('automatic_door_rate', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Price for 2nd, 3rd, 4th automatic doors
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Manual Door
                  </label>
                  <div className="flex items-center">
                    <span className="text-gray-500 mr-2">£</span>
                    <input
                      type="number"
                      step="0.01"
                      value={config.manual_door_rate}
                      onChange={(e) => updateField('manual_door_rate', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Price per manual door (never gets base rate)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Automatic Gate
                  </label>
                  <div className="flex items-center">
                    <span className="text-gray-500 mr-2">£</span>
                    <input
                      type="number"
                      step="0.01"
                      value={config.automatic_gate_rate}
                      onChange={(e) => updateField('automatic_gate_rate', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Additional gates (1st automatic gets base rate)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Automatic Barrier
                  </label>
                  <div className="flex items-center">
                    <span className="text-gray-500 mr-2">£</span>
                    <input
                      type="number"
                      step="0.01"
                      value={config.automatic_barrier_rate}
                      onChange={(e) => updateField('automatic_barrier_rate', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Automatic Shutter
                  </label>
                  <div className="flex items-center">
                    <span className="text-gray-500 mr-2">£</span>
                    <input
                      type="number"
                      step="0.01"
                      value={config.automatic_shutter_rate}
                      onChange={(e) => updateField('automatic_shutter_rate', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contract Length Discounts */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Contract Length Discounts</h3>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    2 Year Contract
                  </label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      step="0.1"
                      value={config.discount_2yr}
                      onChange={(e) => updateField('discount_2yr', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                    <span className="text-gray-500 ml-2">%</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    3 Year Contract
                  </label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      step="0.1"
                      value={config.discount_3yr}
                      onChange={(e) => updateField('discount_3yr', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                    <span className="text-gray-500 ml-2">%</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    4 Year Contract
                  </label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      step="0.1"
                      value={config.discount_4yr}
                      onChange={(e) => updateField('discount_4yr', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                    <span className="text-gray-500 ml-2">%</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    5 Year Contract
                  </label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      step="0.1"
                      value={config.discount_5yr}
                      onChange={(e) => updateField('discount_5yr', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                    <span className="text-gray-500 ml-2">%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bi-Annual Service Bonus */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Bi-Annual Service Discount</h3>
              
              <div className="max-w-xs">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Additional Discount for 2x Visits Per Year
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    step="0.1"
                    value={config.bi_annual_bonus}
                    onChange={(e) => updateField('bi_annual_bonus', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                  <span className="text-gray-500 ml-2">%</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Added on top of contract length discount
                </p>
              </div>
            </div>
          </div>

          {/* Call-Out Rates (for reference) */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">PAYG Call-Out Rates</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Standard Call-Out</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        1st Hour On Site
                      </label>
                      <div className="flex items-center">
                        <span className="text-gray-500 mr-2">£</span>
                        <input
                          type="number"
                          step="0.01"
                          value={config.callout_first_hour}
                          onChange={(e) => updateField('callout_first_hour', e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Additional Hours
                      </label>
                      <div className="flex items-center">
                        <span className="text-gray-500 mr-2">£</span>
                        <input
                          type="number"
                          step="0.01"
                          value={config.callout_additional_hour}
                          onChange={(e) => updateField('callout_additional_hour', e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        />
                        <span className="text-gray-500 ml-2">/hr</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Emergency Call-Out</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        1st Hour On Site
                      </label>
                      <div className="flex items-center">
                        <span className="text-gray-500 mr-2">£</span>
                        <input
                          type="number"
                          step="0.01"
                          value={config.emergency_first_hour}
                          onChange={(e) => updateField('emergency_first_hour', e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Additional Hours
                      </label>
                      <div className="flex items-center">
                        <span className="text-gray-500 mr-2">£</span>
                        <input
                          type="number"
                          step="0.01"
                          value={config.emergency_additional_hour}
                          onChange={(e) => updateField('emergency_additional_hour', e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        />
                        <span className="text-gray-500 ml-2">/hr</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex items-center justify-end space-x-3">
            {saved && (
              <span className="text-sm text-green-600 font-medium">✓ Saved</span>
            )}
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 bg-indigo-600 text-white rounded font-medium hover:bg-indigo-700 disabled:opacity-50 transition"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SettingsPPMPricing;
